import {DataResult, process, State} from "@progress/kendo-data-query"
import {setExpandedState, setGroupIds} from "@progress/kendo-react-data-tools"
import {Grid, GridColumn, GridExpandChangeEvent} from "@progress/kendo-react-grid"
import {Checkbox} from "@progress/kendo-react-inputs"
import React, {useEffect, useState} from "react"
import {GLOBAL_ADMIN} from "../../../Constants"
import {setAssignedDealers, UserDealer} from "../../../containers/User/userSlice"
import {RootState} from "../../../rootReducer"
import {LoadingPanel} from "../../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../../store"

export interface UserRegionDealers {
    regionId: string
    dealerId: string
}

const AssignUserDealer = () => {
    const dispatch = useAppDispatch()
    const userDealers = useAppSelector((state: RootState) => state.containers.users.userDealers)
    const selectedUser = useAppSelector((state: RootState) => state.containers.users.selectedUser)
    const selectedUserRole = useAppSelector((state: RootState) => state.containers.users.selectedUserRole?.value)
    const userRoleLoading = useAppSelector((state: RootState) => state.containers.users.userRoleLoading)
    const userAllRegionsAndDealers = useAppSelector((state: RootState) => state.application.userAllRegionsAndDealers)
    const [selectedDealers, setSelectedDealers] = useState<UserRegionDealers[]>([])
    const [collapsedState, setCollapsedState] = React.useState<string[]>([])
    const isuserDealerUpdateLoading = useAppSelector(
        (state: RootState) => state.containers.users.userDealerUpdateLoader,
    )

    const initialDataState = {
        group: [
            {
                field: "regionName",
            },
        ],
    }

    const processWithGroups = (dealerdata: UserDealer[] | null, initialDataState: State) => {
        const data = dealerdata ?? []
        const newDataState = process(
            data.filter((userDealer: UserDealer) =>
                userAllRegionsAndDealers.some(all => all.dealerId === parseInt(userDealer.dealerId)),
            ),
            initialDataState,
        )

        setGroupIds({data: newDataState.data, group: initialDataState.group})

        return newDataState
    }
    const [groupedData, setGroupedData] = useState<DataResult | null>(processWithGroups(userDealers, initialDataState))

    // select dealers that have been assigned to user
    useEffect(() => {
        userDealers && setGroupedData(processWithGroups(userDealers, initialDataState))
        let dealers: UserRegionDealers[] = []
        userDealers &&
            userDealers.forEach((dealer: UserDealer) => {
                if (dealer.active) {
                    dealers.push({
                        regionId: dealer.regionID,
                        dealerId: dealer.dealerId,
                    })
                }
            })
        setSelectedDealers(dealers)
        dispatch(setAssignedDealers(dealers.map(d => d.dealerId)))
        return () => setGroupedData(null)
    }, [dispatch, userDealers])

    // select all dealers if role is global admin and user dealers are loaded
    // otherwise, reset selected dealers
    useEffect(() => {
        if (selectedUserRole === GLOBAL_ADMIN && userDealers) {
            let dealers: UserRegionDealers[] = []
            userDealers.forEach((dealer: UserDealer) => {
                dealers.push({
                    regionId: dealer.regionID,
                    dealerId: dealer.dealerId,
                })
            })
            setSelectedDealers(dealers)
            dispatch(setAssignedDealers(dealers.map(d => d.dealerId)))
        }
    }, [dispatch, selectedUserRole, userDealers])

    const setDealerSelected = (regionId: string, dealerId: string) => {
        let dealers = selectedDealers
        if (dealers.some(d => d.dealerId === dealerId)) {
            dealers.splice(
                dealers.findIndex(d => d.dealerId === dealerId),
                1,
            )
        } else {
            dealers.push({regionId, dealerId})
        }

        setSelectedDealers([...dealers])
        dispatch(setAssignedDealers(dealers.map(d => d.dealerId)))
    }

    const checkBoxColumn = (props: any) => (
        <>
            {props.level > 0 && (
                <td colSpan={1} role="gridcell" aria-colindex={1}>
                    <Checkbox
                        style={{opacity: 1}}
                        onClick={() => setDealerSelected(props.dataItem.regionID, props.dataItem.dealerId)}
                        checked={
                            selectedDealers.some(d => d.dealerId === props.dataItem.dealerId) ||
                            selectedUserRole === GLOBAL_ADMIN
                        }
                        disabled={selectedUserRole === GLOBAL_ADMIN}
                    />
                </td>
            )}
        </>
    )

    const onExpandChange = React.useCallback(
        (event: GridExpandChangeEvent) => {
            const item = event.dataItem
            if (item.groupId) {
                const collapsedIds = !event.value
                    ? [...collapsedState, item.groupId]
                    : collapsedState.filter(groupId => groupId !== item.groupId)
                setCollapsedState(collapsedIds)
            }
        },
        [collapsedState],
    )

    const newData = setExpandedState({
        data: groupedData?.data,
        collapsedIds: collapsedState,
    })
    return (
        <>
            {isuserDealerUpdateLoading && <LoadingPanel />}
            <div id="userDealerGrid" className="pe-0">
                <Grid
                    sortable={true}
                    data={newData}
                    {...initialDataState}
                    onExpandChange={onExpandChange}
                    expandField="expanded"
                    style={{
                        height: `calc(100vh - 1${
                            selectedUser.firstName !== null &&
                            selectedUser.lastName !== null &&
                            selectedUserRole !== undefined
                                ? "7"
                                : "4"
                        }.5rem)`,
                    }}
                    className={userRoleLoading ? "k-state-disabled" : ""}
                >
                    <GridColumn width={90} cell={props => checkBoxColumn(props)} />
                    <GridColumn
                        field="dealerNumber"
                        title="Dealer Number"
                        className="k-column-align-center"
                        width={200}
                    />
                    <GridColumn field="dealerName" title="Dealer" />
                </Grid>
            </div>
        </>
    )
}

export default AssignUserDealer
